// duration-derive.ts — pure helpers for the task duration field.
// Lets users express durations in natural units (giờ/phút) instead of always
// converting to raw minutes. Storage stays in minutes (estimateMinutes).

/**
 * Parse a free-text duration into minutes. Returns null when unparseable so the
 * caller can keep the user's in-progress text instead of forcing a value.
 *
 * Accepted forms (case-insensitive, comma = dot):
 *   "90"                 → 90  (bare number = minutes)
 *   "2h", "2 giờ", "2g"  → 120
 *   "1h30", "1 giờ 30"   → 90  (hours + minutes)
 *   "1.5h", "1,5 giờ"    → 90  (fractional hours)
 *   "45p", "45 phút",    → 45  (explicit minutes)
 *   "8 tiếng"            → 480
 */
export function parseDurationToMinutes(raw: string): number | null {
  const s = raw.trim().toLowerCase().replace(",", ".");
  if (s === "") return null;

  // Bare number → minutes.
  if (/^\d+(\.\d+)?$/.test(s)) return Math.round(parseFloat(s));

  // Hours (optionally + minutes): "2h", "1g30", "1 giờ 30 phút", "8 tiếng".
  const hm = s.match(
    /^(\d+(?:\.\d+)?)\s*(?:h|g|giờ|gio|tiếng|tieng)\s*(?:(\d+)\s*(?:p|ph|phút|phut|m|min|')?)?$/,
  );
  if (hm) {
    const hours = parseFloat(hm[1]);
    const mins = hm[2] ? parseInt(hm[2], 10) : 0;
    return Math.round(hours * 60 + mins);
  }

  // Minutes only with a unit suffix: "90p", "90 phút", "90m".
  const mm = s.match(/^(\d+(?:\.\d+)?)\s*(?:p|ph|phút|phut|m|min|')$/);
  if (mm) return Math.round(parseFloat(mm[1]));

  return null;
}

/** Format minutes as a friendly Vietnamese string ("2 giờ 30 phút"). */
export function formatDuration(min: number): string {
  if (!Number.isFinite(min) || min <= 0) return "0 phút";
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h === 0) return `${m} phút`;
  if (m === 0) return `${h} giờ`;
  return `${h} giờ ${m} phút`;
}
